/**
 * The only execution-state vocabulary in the app: the ten values the backend persists in
 * `currentState`. See `docs/adr/0001-single-execution-state-vocabulary.md`.
 */
export const EXECUTION_STATES = [
  'unassigned',
  'inactive',
  'active',
  'stalled',
  'completed',
  'expired',
  'canceled',
  'complete_expired',
  'incomplete',
  'incomplete_partial',
] as const;

export type ExecutionState = (typeof EXECUTION_STATES)[number];

const EXECUTION_STATE_SET: ReadonlySet<unknown> = new Set(EXECUTION_STATES);

export const isExecutionState = (value: unknown): value is ExecutionState =>
  EXECUTION_STATE_SET.has(value);

/**
 * `null` means the execution has no state: either Firestore has not written `currentState`
 * yet, or it holds a value this app does not know. Callers decide how to render that —
 * the vocabulary itself has no member for it.
 */
export const parseExecutionState = (value: unknown): ExecutionState | null =>
  isExecutionState(value) ? value : null;

/**
 * Named accessor for the states, for call sites that read better with a name than with a
 * bare string — and for the `.js` files, where the type cannot help them.
 */
export const EXECUTION_STATE = Object.fromEntries(
  EXECUTION_STATES.map(state => [state, state]),
) as { readonly [K in ExecutionState]: K };

/** States the backend accepts a cancellation from. */
export const CANCELABLE_EXECUTION_STATES: readonly ExecutionState[] = [
  'unassigned',
  'inactive',
  'active',
  'stalled',
  'expired',
];

/**
 * `setStalled` only accepts these as the source state; from anywhere else the backend
 * rejects the transition, so reporting a delay is offered only here.
 */
export const STALLABLE_EXECUTION_STATES: readonly ExecutionState[] = ['active', 'expired'];

/**
 * A supervisor closure (ADR 0002) is only accepted from these: the window is open or in extra
 * time. Stalled executions must resume first and past days cannot be closed.
 */
export const SUPERVISOR_CLOSABLE_EXECUTION_STATES: readonly ExecutionState[] = ['active', 'expired'];

/**
 * Finished or idle lifecycle states. Plan-progress and assignee-offline delay UI
 * must not appear once the execution has left operational work.
 */
export const EXECUTION_STATES_WITHOUT_DELAY_INDICATOR: readonly ExecutionState[] = [
  'inactive',
  'completed',
  'expired',
  'canceled',
  'complete_expired',
  'incomplete',
  'incomplete_partial',
];

export const shouldShowExecutionDelayIndicator = (currentState: unknown): boolean =>
  isExecutionState(currentState) &&
  !EXECUTION_STATES_WITHOUT_DELAY_INDICATOR.includes(currentState);
